import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  useDepositBonuses,
  useDepositHistory,
  useDepositProviders,
  usePaymentResources,
  useSubmitPayment,
  useWithdrawalProviders,
} from '@/api';
import { isRedepositProvider } from '@/domain/payment-providers';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { EmptyState, Icon, Spinner } from '@/ui/common';
import { DepositFormPanel } from '@/ui/profile';

type DepositFormSearch = {
  /** Set when opening a Redeposit provider from the withdrawals list. */
  from?: 'withdrawals';
};

export const Route = createFileRoute('/profile/deposits/$providerId')({
  validateSearch: (search: Record<string, unknown>): DepositFormSearch => ({
    from: search.from === 'withdrawals' ? 'withdrawals' : undefined,
  }),
  component: ProfileDepositFormPage,
});

function ProfileDepositFormPage() {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { providerId } = Route.useParams();
  const { from } = Route.useSearch();
  const fromWithdrawals = from === 'withdrawals';
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const depositProviders = useDepositProviders(true);
  // Redeposit providers are listed under withdrawals (UsersAPI Redeposit flag),
  // so resolve from that catalog when coming from withdrawals or when missing
  // from the deposit list.
  const needWithdrawalLookup =
    fromWithdrawals ||
    (!depositProviders.isLoading && !depositProviders.findById(providerId));
  const withdrawalProviders = useWithdrawalProviders(needWithdrawalLookup);

  const provider =
    depositProviders.findById(providerId) ?? withdrawalProviders.findById(providerId);
  const isRedeposit = isRedepositProvider(provider) || fromWithdrawals;
  const providersLoading =
    depositProviders.isLoading || (needWithdrawalLookup && withdrawalProviders.isLoading);
  const currency = depositProviders.currency || withdrawalProviders.currency;

  const resources = usePaymentResources('deposit', provider, Boolean(provider));
  const bonuses = useDepositBonuses(provider?.Id ?? null, Boolean(provider));
  const history = useDepositHistory(Boolean(provider));
  const submitPayment = useSubmitPayment();

  const backTo = fromWithdrawals || isRedepositProvider(provider)
    ? '/profile/withdrawals'
    : '/profile/deposits';
  const backLabel =
    backTo === '/profile/withdrawals'
      ? label('Withdraw', 'Withdraw')
      : label('Deposit', 'Deposit');

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      <Link
        to={backTo}
        className="inline-flex w-fit items-center gap-2 text-[13px] text-text-secondary hover:text-text-primary"
      >
        <Icon name="arrow-right" size={16} className="-scale-x-100" />
        {backLabel}
      </Link>

      <header className="flex flex-col gap-1 lg:gap-2">
        <h1 className="text-[18px] font-bold text-text-primary lg:text-[22px]">
          {isRedeposit ? label('Redeposit', 'Redeposit') : label('Deposit', 'Deposit')}
        </h1>
        <p className="text-[13px] text-text-secondary">
          {isRedeposit
            ? label(
                'Move funds back to your sportsbook balance',
                'Move funds back to your sportsbook balance',
              )
            : label('Enter amount and confirm deposit', 'Enter amount and confirm deposit')}
        </p>
      </header>

      {providersLoading ? (
        <div className="flex justify-center py-10">
          <Spinner size="md" />
        </div>
      ) : !provider ? (
        <EmptyState
          icon="deposit"
          title={label('Provider not found', 'Provider not found')}
          description={label(
            'Choose another payment provider from the list.',
            'Choose another payment provider from the list.',
          )}
        />
      ) : (
        <DepositFormPanel
          provider={provider}
          currency={currency}
          resources={resources}
          bonuses={bonuses}
          history={history}
          submitPayment={submitPayment}
          isRedeposit={isRedeposit}
          changeProviderTo={backTo}
        />
      )}
    </div>
  );
}
